export type DrumKey =
  | 'closedHiHat'
  | 'openHiHat'
  | 'pedalHiHat'
  | 'bassDrum'
  | 'snareDrum'
  | 'snareRim'
  | 'crashCymbal'
  | 'rideCymbal'
  | 'highTom'
  | 'midTom'
  | 'lowFloorTom'
  | 'highFloorTom';

export const DRUM_LABELS: Record<DrumKey, string> = {
  closedHiHat: 'Closed Hi-Hat',
  openHiHat: 'Open Hi-Hat',
  pedalHiHat: 'Pedal Hi-Hat',
  bassDrum: 'Kick Drum',
  snareDrum: 'Snare Drum',
  snareRim: 'Snare Rim',
  crashCymbal: 'Crash Cymbal',
  rideCymbal: 'Ride Cymbal',
  highTom: 'High Tom',
  midTom: 'Mid Tom',
  lowFloorTom: 'Low Floor Tom',
  highFloorTom: 'High Floor Tom',
};

export const DRUM_KEYS: readonly DrumKey[] = Object.keys(DRUM_LABELS) as DrumKey[];

const isDrumKey = (value: unknown): value is DrumKey =>
  typeof value === 'string' && value in DRUM_LABELS;

/** One midi note triggers one or two drums — never zero. */
export type MidiDrumMapping = Record<number, DrumKey[]>;

export const MAX_DRUMS_PER_MIDI = 2;

export const DEFAULT_MIDI_TO_DRUM: Readonly<Record<number, readonly DrumKey[]>> = Object.freeze({
  22: ['closedHiHat'],
  26: ['openHiHat'],
  36: ['bassDrum'],
  38: ['snareDrum'],
  40: ['snareRim'],
  41: ['lowFloorTom'],
  42: ['closedHiHat'],
  43: ['highFloorTom'],
  44: ['pedalHiHat'],
  45: ['midTom'],
  46: ['openHiHat'],
  47: ['midTom'],
  48: ['highTom'],
  49: ['crashCymbal'],
  51: ['rideCymbal'],
  52: ['crashCymbal'],
  55: ['crashCymbal'],
  57: ['crashCymbal'],
  59: ['rideCymbal'],
});

/**
 * The one place the per-note invariant is enforced: known drums only, deduped,
 * at most MAX_DRUMS_PER_MIDI. Also accepts the legacy bare `DrumKey`. An empty
 * result means the note maps to nothing and the caller should drop the entry.
 */
export const normalizeDrumList = (value: unknown): DrumKey[] => {
  const drums = Array.isArray(value) ? value : [value];
  return [...new Set(drums.filter(isDrumKey))].slice(0, MAX_DRUMS_PER_MIDI);
};

/**
 * Coerces a persisted mapping into the current shape: unwraps the legacy
 * `Record<number, DrumKey>` form, drops unknown drums, and enforces the
 * 1..MAX_DRUMS_PER_MIDI invariant by dropping now-empty entries.
 */
export const normalizeMidiDrumMapping = (raw: unknown): MidiDrumMapping => {
  const out: MidiDrumMapping = {};
  if (!raw || typeof raw !== 'object') return out;

  for (const [midi, value] of Object.entries(raw)) {
    const drums = normalizeDrumList(value);
    if (drums.length) out[Number(midi)] = drums;
  }
  return out;
};

export const cloneDefaultMidiDrumMapping = (): MidiDrumMapping =>
  normalizeMidiDrumMapping(DEFAULT_MIDI_TO_DRUM);
